import React from 'react';
import { Button, FormInput } from '@getro/rombo';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Formik, Form, Field } from 'formik';
import { ChevronRight, MessagesSquare, Plus, SlidersHorizontal } from 'lucide-react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Link, useLocation, useHistory } from 'react-router-dom';
import * as Yup from 'yup';

import isEqual from 'lodash.isequal';
import { companyDefaultFilters } from 'components/organisms/companyFilters/defaultFilters';
import { reverseParsedFilters } from 'services/contacts/filters';
import { useEditCompanyList } from '../hooks/useEditCompanyList';
import { activeNetworkSelector } from '../../../redux/selectors';

export const EditCompanyList = ({ routePath, sharedList, closeModal }) => {
  const { initialValues, updateSharedList, isUpdatingList } = useEditCompanyList(sharedList);
  const activeNetwork = useSelector(activeNetworkSelector);
  const { search } = useLocation();
  const history = useHistory();
  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
  });

  const onSubmit = async (values) => {
    updateSharedList({
      ...values,
    }).then(() => {
      history.push(`/networks/${activeNetwork.slug}/list/${sharedList.id}/contacts`);
    });
  };

  const listItems = [
    {
      title: 'Ask for intros',
      description:
        'Share a link so others can view your target companies, connect their LinkedIn, and quickly spot connections to offer introductions.',
      icon: MessagesSquare,
      link: `${routePath}/ask-for-intros${search}`,
      showActiveState: true,
      isActive: sharedList.shareable,
      manageText: sharedList.shareable ? 'Edit' : 'Enable',
    },
    {
      title: 'Saved filters',
      description:
        'Set and save filters to focus on relevant contacts. Saved filters are applied and displayed on the shared view, helping others understand your goals.',
      icon: SlidersHorizontal,
      showActiveState: true,
      isActive: !isEqual(
        { ...companyDefaultFilters, ...reverseParsedFilters(sharedList.smartSharedList?.filters, ['companyIds']) },
        companyDefaultFilters,
      ),
      onClick: () => {
        closeModal();
        window.dispatchEvent(new Event('onShowContactsFilters'));
        window.CommandBar.triggerNudge(13251);
      },
      manageText: 'Manage filters',
    },
    {
      title: 'Manage companies in list',
      description: "Edit the companies you'd like in this list to request introductions.",
      icon: Plus,
      link: `${routePath}/add-companies${search}`,
      manageText: '',
    },
  ];

  return (
    <Box>
      <Text fontSize="20px" fontWeight="semibold" color="text.dark" lineHeight="28px">
        List settings
      </Text>
      <Flex sx={{ gap: '80px', mt: '32px', flexDirection: 'column' }}>
        <Formik initialValues={{ name: initialValues.name }} onSubmit={onSubmit} validationSchema={validationSchema}>
          {({ values }) => (
            <Box as={Form} sx={{ display: 'flex', gap: '8px', alignItems: 'end' }}>
              <Field name="name" component={FormInput} label="Name" size="small" />
              <Button
                type="submit"
                size="small"
                width="100px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                loading={isUpdatingList}
                disabled={isEqual(values.name, initialValues.name)}
              >
                Update
              </Button>
            </Box>
          )}
        </Formik>
        <Flex sx={{ gap: '32px', flexDirection: 'column' }}>
          {listItems.map((item) => (
            <Flex
              as={item.link ? Link : 'div'}
              to={item.link}
              onClick={item.onClick}
              sx={{
                gap: '16px',
                textDecoration: 'none',
                justifyContent: 'space-between',
                cursor: 'pointer',
              }}
            >
              <Flex sx={{ gap: '16px' }}>
                <Box flex="0 0 auto" sx={{ color: 'text.subtle' }}>
                  <Box
                    as={item.icon}
                    width="24px"
                    height="24px"
                    aria-hidden="true"
                    sx={{ color: item.isActive ? 'purple.400' : 'neutral.300' }}
                  />
                </Box>
                <Flex sx={{ flexDirection: 'column', gap: '4px' }}>
                  <Flex sx={{ alignItems: 'center', gap: '8px' }}>
                    <Text fontSize="14px" fontWeight="500" color="text.dark" lineHeight="20px">
                      {item.title}
                    </Text>
                    {item.showActiveState && (
                      <Flex
                        sx={{
                          backgroundColor: !item.isActive ? 'neutral.50' : 'green.600',
                          borderRadius: '100px',
                          p: '4px 8px',
                          color: !item.isActive ? 'neutral.700' : 'white',
                          textTransform: 'uppercase',
                          fontSize: '12px',
                          fontWeight: 500,
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '8px',
                          height: '20px',
                        }}
                      >
                        <Box as="p" lineHeight={1}>
                          {!item.isActive ? 'off' : 'on'}
                        </Box>
                      </Flex>
                    )}
                  </Flex>
                  <Box fontSize="14px" lineHeight="20px" color="text.main" maxWidth="416px">
                    {item.description}
                  </Box>
                </Flex>
              </Flex>
              <Flex
                sx={{
                  alignSelf: 'center',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  minWidth: '130px',
                  justifyContent: 'flex-end',
                }}
              >
                {item.manageText && (
                  <Box fontSize="14px" fontWeight="500" lineHeight="20px" color="text.main">
                    {item.manageText}
                  </Box>
                )}
                <Box as={ChevronRight} size="16px" aria-hidden="true" sx={{ strokeWidth: '1.5' }} />
              </Flex>
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Box>
  );
};

EditCompanyList.propTypes = {
  routePath: PropTypes.string.isRequired,
  sharedList: PropTypes.object,
  closeModal: PropTypes.func,
};

EditCompanyList.defaultProps = {
  sharedList: {},
  closeModal: () => {},
};
